/**
 * Wraps a single HTMLVideoElement as the master playback clock.
 *
 * The viewer always has at most one active <video> — the currently selected
 * camera's stream. switchCamera() swaps `src`, waits for loadedmetadata,
 * seeks to the saved time, and resumes play if we were playing.
 *
 * Time semantics: video.currentTime is in seconds from the beginning of the
 * MP4. The pose timeline is also in seconds (from poses.ts0), and the
 * exporter writes `ts` values that are PTS-derived — so they align to
 * video.currentTime modulo any offset between the cached `start` frame and
 * the video's own start. For the long360 dataset, both start at 0.
 */
export class VideoClock {
  readonly el: HTMLVideoElement;
  private wasPlaying = false;

  constructor(el: HTMLVideoElement) {
    this.el = el;
    el.muted = true;
    el.playsInline = true;
    el.preload = "auto";
  }

  get currentTime(): number {
    return this.el.currentTime;
  }

  get duration(): number {
    return Number.isFinite(this.el.duration) ? this.el.duration : 0;
  }

  get paused(): boolean {
    return this.el.paused;
  }

  async play(): Promise<void> {
    try { await this.el.play(); } catch (e) { console.warn("play():", e); }
  }

  pause(): void {
    this.el.pause();
  }

  seek(seconds: number): void {
    this.el.currentTime = Math.max(0, Math.min(seconds, this.duration || seconds));
  }

  /** Swap to a new mp4, preserving currentTime + play-state across the switch. */
  async switchCamera(url: string): Promise<void> {
    this.wasPlaying = !this.el.paused;
    const keepTime = this.el.currentTime || 0;
    this.el.pause();
    this.el.src = url;
    // Wait for loadedmetadata so we know currentTime accepts a seek.
    await new Promise<void>((resolve, reject) => {
      const ok = () => { cleanup(); resolve(); };
      const fail = () => { cleanup(); reject(new Error(`video load failed: ${url}`)); };
      const cleanup = () => {
        this.el.removeEventListener("loadedmetadata", ok);
        this.el.removeEventListener("error", fail);
      };
      this.el.addEventListener("loadedmetadata", ok, { once: true });
      this.el.addEventListener("error", fail, { once: true });
    });
    this.el.currentTime = keepTime;
    if (this.wasPlaying) await this.play();
  }
}
